import { Box } from "@chakra-ui/react";
// Note: EspdRequirementGroupDto.subGroups is typed as `any[]` in the generated code
// because http4k can't express the recursive self-reference in the OpenAPI example.
// At runtime it's still EspdRequirementGroupDto[] — just not statically checked.
import { EspdRequirementGroupDto, EspdRequirementResponseDto } from "../../../../autogen/bff-api";
import { EspdRequirementField } from "./EspdRequirementField";

interface Props {
  group: EspdRequirementGroupDto;
  responses: Map<string, EspdRequirementResponseDto>;
  onResponseChange: (requirementId: string, value: string | boolean | number | null, instanceIndex?: number | null) => void;
  onMetadataChange?: (requirementId: string, field: string, value: string | null, instanceIndex?: number | null) => void;
  readOnly?: boolean;
  parentIndicatorValue?: boolean | null;
  instanceIndex?: number | null;
  isValidating?: boolean;
}

function responseKey(requirementId: string, instanceIndex: number | null | undefined): string {
  return instanceIndex != null ? `${requirementId}_i${instanceIndex}` : requirementId;
}

export const EspdRequirementGroupForm = ({ group, responses, onResponseChange, onMetadataChange, readOnly, parentIndicatorValue, instanceIndex, isValidating }: Props) => {
  if (group.processingInstruction) {
    if (group.processingInstruction === "GROUP_FULFILLED.ON_TRUE" && parentIndicatorValue !== true) {
      return null;
    }
    if (group.processingInstruction === "GROUP_FULFILLED.ON_FALSE" && parentIndicatorValue !== false) {
      return null;
    }
  }

  const indicatorRequirement = group.requirements.find((r) => r.responseDataType === "INDICATOR");
  const indicatorResponse = indicatorRequirement ? responses.get(responseKey(indicatorRequirement.id, instanceIndex)) : undefined;
  const currentIndicatorValue = indicatorResponse?.indicator ?? null;

  const groupHasErrors = isValidating && group.requirements.some((req) => {
    const response = responses.get(responseKey(req.id, instanceIndex));
    return !req.optional && !isAnswered(response, req.responseDataType);
  });

  return (
    <Box ml="2" pl="3" py="2" borderLeft="3px solid" borderColor={groupHasErrors ? "orange.400" : "teal.200"} bg="white" mb="2">
      {group.requirements.map((req) => {
        const response = responses.get(responseKey(req.id, instanceIndex));
        const currentValue = getResponseValue(response, req.responseDataType);
        const hasError = isValidating && !req.optional && !isAnswered(response, req.responseDataType);

        return (
          <EspdRequirementField
            key={req.id}
            requirement={req}
            value={currentValue}
            response={response}
            onChange={onResponseChange}
            onMetadataChange={onMetadataChange}
            readOnly={readOnly}
            instanceIndex={instanceIndex}
            hasError={hasError}
          />
        );
      })}

      {group.subGroups.map((subGroup) => (
        <EspdRequirementGroupForm
          key={subGroup.id}
          group={subGroup}
          responses={responses}
          onResponseChange={onResponseChange}
          onMetadataChange={onMetadataChange}
          readOnly={readOnly}
          parentIndicatorValue={currentIndicatorValue}
          instanceIndex={instanceIndex}
          isValidating={isValidating}
        />
      ))}
    </Box>
  );
};

function getResponseValue(
  response: EspdRequirementResponseDto | undefined,
  dataType: string
): string | boolean | number | null | undefined {
  if (!response) return undefined;

  switch (dataType) {
    case "INDICATOR":
      return response.indicator;
    case "DATE":
      return response.date;
    case "DESCRIPTION":
      return response.description;
    case "AMOUNT":
      return response.amount;
    case "CODE":
    case "CODE_COUNTRY":
      return response.code;
    case "PERCENTAGE":
      return response.percentage;
    case "PERIOD":
      return response.period;
    case "QUANTITY":
    case "QUANTITY_INTEGER":
    case "QUANTITY_YEAR":
      return response.quantity;
    case "EVIDENCE_URL":
      return response.evidenceUrl;
    default:
      return response.description;
  }
}

function isAnswered(
  response: EspdRequirementResponseDto | undefined,
  dataType: string
): boolean {
  if (!response) return false;

  switch (dataType) {
    case "INDICATOR":
      return response.indicator === true || response.indicator === false;
    case "DATE":
      return response.date != null && response.date !== "";
    case "DESCRIPTION":
      return response.description != null && response.description !== "";
    case "AMOUNT":
      return response.amount != null;
    case "CODE":
    case "CODE_COUNTRY":
      return response.code != null && response.code !== "";
    case "PERCENTAGE":
      return response.percentage != null;
    case "PERIOD":
      return response.period != null && response.period !== "";
    case "QUANTITY":
    case "QUANTITY_INTEGER":
    case "QUANTITY_YEAR":
      return response.quantity != null;
    case "EVIDENCE_URL":
      return response.evidenceUrl != null && response.evidenceUrl !== "";
    default:
      return response.description != null && response.description !== "";
  }
}
